import React, { Component, ReactNode, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// Type declaration for gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackDescription?: string;
  level?: 'app' | 'page' | 'component';
  showDetails?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorFallbackProps extends ErrorBoundaryState {
  onReset: () => void;
  title: string;
  description: string;
  level: 'app' | 'page' | 'component';
  showDetails: boolean;
}

// Error Fallback UI Component
const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  errorId,
  onReset,
  title,
  description,
  level,
  showDetails
}) => {
  const getLevelConfig = () => {
    switch (level) {
      case 'app':
        return {
          icon: AlertTriangle,
          variant: 'destructive' as const,
          size: 'h-12 w-12',
          actions: [
            { label: 'Reload Application', action: () => window.location.reload(), icon: RefreshCw },
            { label: 'Go Home', action: () => window.location.href = '/', icon: Home }
          ]
        };
      case 'page':
        return {
          icon: AlertTriangle,
          variant: 'destructive' as const,
          size: 'h-8 w-8',
          actions: [
            { label: 'Try Again', action: onReset, icon: RefreshCw },
            { label: 'Go Home', action: () => window.location.href = '/', icon: Home }
          ]
        };
      case 'component':
        return {
          icon: Bug,
          variant: 'secondary' as const,
          size: 'h-6 w-6',
          actions: [
            { label: 'Retry', action: onReset, icon: RefreshCw }
          ]
        };
    }
  };

  const config = getLevelConfig();
  const IconComponent = config.icon;

  return (
    <div className="flex items-center justify-center p-4 min-h-[200px]">
      <Card className={`max-w-md w-full ${level === 'app' ? 'border-destructive' : ''}`}>
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <IconComponent className={`${config.size} text-destructive`} />
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <CardTitle className="text-lg">{title}</CardTitle>
            <Badge variant={config.variant}>{level.toUpperCase()}</Badge>
          </div>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Action Buttons */}
          <div className="flex flex-col gap-2">
            {config.actions.map((action, index) => {
              const ActionIcon = action.icon;
              return (
                <Button
                  key={index}
                  onClick={action.action}
                  variant={index === 0 ? "default" : "outline"}
                  className="w-full"
                >
                  <ActionIcon className="w-4 h-4 mr-2" />
                  {action.label}
                </Button>
              );
            })}
          </div>

          {/* Error Details (Development/Debug) */}
          {showDetails && error && (
            <details className="mt-4 p-3 bg-muted rounded-lg text-sm">
              <summary className="cursor-pointer font-medium text-muted-foreground mb-2">
                🐛 Error Details (ID: {errorId.slice(0, 8)})
              </summary>
              <div className="space-y-2 text-xs">
                <div>
                  <strong>Error:</strong> {error.name}
                </div>
                <div>
                  <strong>Message:</strong> {error.message}
                </div>
                {error.stack && (
                  <div>
                    <strong>Stack:</strong>
                    <pre className="mt-1 p-2 bg-background rounded text-xs overflow-auto max-h-32">
                      {error.stack}
                    </pre>
                  </div>
                )}
                {errorInfo?.componentStack && (
                  <div>
                    <strong>Component Stack:</strong>
                    <pre className="mt-1 p-2 bg-background rounded text-xs overflow-auto max-h-32">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}

          {/* Help Text */}
          <div className="text-xs text-muted-foreground text-center">
            {level === 'app' && "The application encountered an unexpected error. Please reload to continue."}
            {level === 'page' && "This page encountered an error. You can try again or navigate elsewhere."}
            {level === 'component' && "A component failed to load. This won't affect the rest of the application."}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Error Boundary Class Component
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error for monitoring/debugging
    console.error('🚨 ErrorBoundary caught an error:', {
      error,
      errorInfo,
      level: this.props.level || 'component',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    });

    // Update state with error info
    this.setState({
      error,
      errorInfo
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: Send error to a monitoring service if one is configured.
    // Example: monitoringService.captureException(error, { extra: errorInfo });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          {...this.state}
          onReset={this.handleReset}
          title={this.props.fallbackTitle || 'Something went wrong'}
          description={this.props.fallbackDescription || 'An unexpected error occurred while rendering this section.'}
          level={this.props.level || 'component'}
          showDetails={this.props.showDetails ?? (import.meta.env.MODE === 'development')}
        />
      );
    }

    return this.props.children;
  }
}

// Specialized Error Boundary Components for different levels
export const AppErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary
    level="app"
    fallbackTitle="Application Error"
    fallbackDescription="NovelSprint encountered an unexpected error. Please reload the application."
    showDetails={import.meta.env.MODE === 'development'}
  >
    {children}
  </ErrorBoundary>
);

export const PageErrorBoundary: React.FC<{ 
  children: ReactNode; 
  pageName?: string 
}> = ({ children, pageName }) => (
  <ErrorBoundary
    level="page"
    fallbackTitle={`${pageName || 'Page'} Error`}
    fallbackDescription="This page failed to load properly. You can try again or navigate to another page."
    showDetails={import.meta.env.MODE === 'development'}
  >
    {children}
  </ErrorBoundary>
);

export const ComponentErrorBoundary: React.FC<{ 
  children: ReactNode; 
  componentName?: string 
}> = ({ children, componentName }) => (
  <ErrorBoundary
    level="component"
    fallbackTitle={`${componentName || 'Component'} Error`}
    fallbackDescription="This component failed to render. The rest of the application should work normally."
    showDetails={false}
  >
    {children}
  </ErrorBoundary>
);

// Hook for functional components to report errors
export const useErrorHandler = () => {
  const [, setError] = React.useState<Error>();
  
  return React.useCallback((error: Error) => {
    console.error('🚨 Manual error report:', error);
    setError(() => {
      throw error;
    });
  }, []);
};

export default ErrorBoundary;

// Spezielle Error-Boundary für Chunk-Loading-Fehler
export class ChunkLoadErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    const normalizedMessage = String(error?.message || '').toLowerCase();
    // Check if it's a chunk loading error
    const isChunkLoadError = 
      normalizedMessage.includes('loading chunk') ||
      normalizedMessage.includes('failed to fetch dynamically imported module') ||
      normalizedMessage.includes('error loading dynamically imported module') ||
      normalizedMessage.includes('chunkloaderror') ||
      normalizedMessage.includes('mime type') ||
      normalizedMessage.includes('loading css chunk');
    
    if (isChunkLoadError) {
      // Clear caches and reload for chunk errors
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
        });
      }
      // Clear session storage to allow retry
      sessionStorage.removeItem('ns_chunk_reload');
      window.location.reload();
      return { hasError: true, error };
    }
    
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ChunkLoadErrorBoundary caught an error:', error, errorInfo);
    
    // Report to error tracking service if available
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-bold mb-4">Loading Error</h2>
            <p className="text-muted-foreground mb-4">
              There was an error loading the application. Refreshing...
            </p>
            <Button onClick={() => window.location.reload()}>
              Refresh Now
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
} 
